/** @format */

import {
	Chain,
	NestProductResponse,
	ProcessedProduct,
	ProductsData,
	ProductSku,
	PromotionDetailsMapV1,
	PromotionDetailsMapV2,
	StyleDefiningAttribute,
	StyleDescriptiveAttribute
} from '@shared/data-access/interfaces';
import { Injectable, makeStateKey, TransferState } from '@angular/core';
import { ProductDetailsByUrlResponse, SkuEDDResponse } from '@pdp/data-access/interfaces';

import { HttpService, UtilityService } from '@shared/data-access/services';

import { Observable } from 'rxjs';
import { ProductDao } from './product.dao';
import { TimeService } from '@pdp/utils';

import { map } from 'rxjs/operators';
import { plaCheck } from '@shared/data-access/constants';

@Injectable({
	providedIn: 'root'
})
export class ProductService {
	readonly PRODUCT_BY_ID_STATE_KEY_PREFIX = 'product-';

	constructor(
		private productDao: ProductDao,
		private chain: Chain,
		private state: TransferState,
		private utilityService: UtilityService,
		private timeService: TimeService,
		private httpService: HttpService
	) {}

	/**
	 * @description
	 * Accepts an array of descriptive attributes and returns a list of displayable ones if they exist, otherwise an empty
	 * array is returned.
	 */
	getDisplayableDescriptiveAttributesQV(
		descriptiveAttributes: StyleDescriptiveAttribute[]
	): StyleDescriptiveAttribute[] {
		// Displayable attributes
		const displayableAttributes: StyleDescriptiveAttribute[] = [];

		// Ensure there are attributes to iterate
		if (Array.isArray(descriptiveAttributes) && descriptiveAttributes.length > 0) {
			// Iterate the attributes
			for (let i = 0; i < descriptiveAttributes.length; i += 1) {
				// Check to see if the attribute is displayable
				if (this.utilityService.isTruthy(descriptiveAttributes[i]?.isDisplayable)) {
					// check if dsg pro tips is in the array and pro tips flag is true
					if (
						descriptiveAttributes[i].name.toLowerCase() === 'dsg pro tips' &&
						this.chain.proTipsFlag
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					} else if (
						descriptiveAttributes[i].name.toLocaleLowerCase() !== 'dsg pro tips'
					) {
						// Push the attribute to the return array
						displayableAttributes.push(descriptiveAttributes[i]);
					}
				}
			}
		}

		// Return the displayable attributes
		return displayableAttributes;
	}

	public getAddOnwithNestSkuByEcode$(
		eCodeList: string,
		channel: string,
		version = 'v1',
		requestType?: string
	): Observable<NestProductResponse> {
		return this.productDao.getAddOnSkuByEcode$(eCodeList, channel, 1, version, requestType);
	}

	/**
	 * @description
	 * Returns Promotion details for a given Product ID
	 */
	public getPromotionsByProductId$(
		catEntryId: string | number,
		channelId: string,
		skuIdList?: string | string[],
		useNewPricingService?: boolean,
		retries?: number
	): Observable<PromotionDetailsMapV1 | PromotionDetailsMapV2> {
		return this.productDao.getPromotionsByProductId$(
			catEntryId,
			channelId,
			skuIdList,
			useNewPricingService,
			retries
		);
	}

	/**
	 * @returns True or False if product is coming soon
	 *
	 * @description
	 * Checks to see if product is coming soon
	 *
	 */
	public isProductComingSoon(stringDate: string): boolean {
		if (!stringDate) {
			return false;
		}

		const currentDate = this.timeService.getCurrentTime();

		const comingSoonDate = this.timeService.parseDateFromMDY(stringDate);
		return Boolean(comingSoonDate > currentDate);
	}

	public setProductInStockReminder(
		userEmail: string,
		skuId: string,
		productTitle: string,
		productURL: string,
		imageURL: string,
		attributes: { label: string; value: string }[],
		storeNumber: string,
		storeName?: string,
		retries = 0
	): Observable<unknown> {
		return this.productDao.setProductInStockReminder$(
			userEmail,
			skuId,
			productTitle,
			productURL,
			imageURL,
			attributes,
			storeNumber,
			storeName,
			retries
		);
	}

	/**
	 * Determines if the PLA should be displayed
	 * @param referrer
	 * @param route
	 */
	public setupPla(referrer: string, route: any) {
		let isPLAEnabled = false;
		let campID = '';
		let emailPLA: boolean;

		// Check to see if coming from Google PLA
		if (typeof referrer === 'string' && referrer.indexOf('google.com') > -1) {
			isPLAEnabled = true;
		}

		// set the flag isPLAEnabled to true if the current URL has camp=CSE:DSG query param.
		if (typeof route?.queryParams?._value !== 'undefined') {
			// Get the camp query parameter
			campID = route.queryParams._value.camp;

			if (
				campID &&
				(campID.indexOf('CSE') > -1 ||
					campID.indexOf('SEM') > -1 ||
					campID.indexOf('SNS') > -1)
			) {
				isPLAEnabled = true;
			}

			// Get the EmailPLA query parameter
			emailPLA = this.utilityService.isTruthy(route.queryParams._value.EmailPLA);

			if (emailPLA) {
				isPLAEnabled = true;
			}
		}

		return isPLAEnabled;
	}

	/**
	 * PLA check that we use when isSSRPLAEnabled is true, works on client and server.
	 * @param isPlatformBrowser
	 */
	public isPLA(isPlatformBrowser: boolean): boolean {
		const referrer = isPlatformBrowser
			? document?.referrer
			: this.httpService.getRequestHeader('referer'); // one r referer on purpose

		const queryString = this.httpService.getQueryString();
		const campParam = this.httpService.getParameterValuesFromQueryString(queryString, 'camp');
		const emailPLAParam = this.httpService.getParameterValuesFromQueryString(
			queryString,
			'EmailPLA'
		);

		return plaCheck(referrer, campParam, emailPLAParam);
	}

	/**
	 * @description
	 * Will return defaultColor if product is defined
	 * @param product
	 */
	public determineProductDefaultColor(product: ProcessedProduct): string | undefined {
		if (typeof product !== 'undefined' && product !== null) {
			// Default the selected color attribute
			return product.defaultColor;
		}
		return undefined;
	}

	public getColorForSkuId(skus: ProductSku[], skuId: string): string | undefined {
		const sku = skus?.find((s) => s.partNumber === skuId);
		if (!sku) {
			return undefined;
		}

		const colorAttribute = sku.definingAttributes?.find(
			(attr) => typeof attr.name === 'string' && attr.name.trim().toLowerCase() === 'color'
		);

		return colorAttribute?.value;
	}

	/** Quick View Logic */
	public getDefaultColorFromDefiningAttribute(defAttribute: StyleDefiningAttribute): string {
		if (defAttribute.name.trim().toLowerCase() === 'color') {
			if (defAttribute.value.indexOf('|') > -1) {
				// Get the first color
				return defAttribute.value.substring(0, defAttribute.value.indexOf('|'));
			} else {
				// Set the only available color
				return defAttribute.value;
			}
		}
	}

	/**
	 * @description
	 * Gets the Page object by an url and type.  Types: `category`, `product`, `name`.  The returned Page object will
	 * contain all product data related information. both ECODE and SKU level
	 */
	public getProductByUrl$(
		url: string,
		partNumber: string,
		channel: string,
		clearanceFlag?: string,
		antiClearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries?: number
	): Observable<ProductDetailsByUrlResponse> {
		const STATE_KEY = makeStateKey<ProductDetailsByUrlResponse>(
			`product-url-${this.PRODUCT_BY_ID_STATE_KEY_PREFIX}${url}-${categoryId || ''}-${
				clearanceFlag || ''
			}`
		);

		// Attempt to get the state by key
		const stateData = this.state.get(STATE_KEY, null);
		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		return this.productDao
			.getProductByUrl$(
				url,
				partNumber,
				channel,
				clearanceFlag,
				antiClearanceFlag,
				categoryId,
				productParamType,
				retries
			)
			.pipe(
				map((tempProduct: ProductDetailsByUrlResponse) => {
					const product: ProductDetailsByUrlResponse =
						tempProduct !== null ? Object.assign(tempProduct) : undefined;
					// Set the state
					this.state.set<ProductDetailsByUrlResponse>(
						STATE_KEY,
						structuredClone(product)
					);
					return product;
				})
			);
	}

	public getProductByEcodeV4$(productId: string, checkClearance = false, retries = 0) {
		return this.productDao
			.getProductByEcodeV4$(productId, checkClearance, retries)
			.pipe(map((tempProductResponse) => tempProductResponse?.productsData[0]));
	}

	public static generateImageUrlFromAttributes(productDetails: ProductsData, extraParams = '') {
		const base = `https://dks.scene7.com/is/image/dkscdn/${productDetails.style.partNumber}`;

		const attributes = productDetails.style.definingAttributes;

		const imageParams = `?$UTPMain$${extraParams}`;

		const suffix = `is${imageParams}`;

		for (let attributeIndex = 0; attributeIndex < attributes.length; attributeIndex++) {
			const currentAttribute = attributes[attributeIndex];

			if (currentAttribute.name.toLowerCase() === 'color') {
				const colorArray = currentAttribute.value.split('|');
				let sanitizedColor = colorArray[0].replace(/\//g, '_');
				sanitizedColor = sanitizedColor.replace(/ /g, '_');
				return `${base}_${sanitizedColor}_${suffix}`;
			}
		}

		return `${base}${imageParams}`;
	}

	public static generateDisplayPrice(price: any) {
		return {
			isSeePriceInCart: price.minofferprice < price.thresholdPrice,
			isRangePrice: price.minofferprice !== price.maxofferprice,
			lowestPrice: price.minofferprice,
			highestPrice: price.maxofferprice
		};
	}

	/**
	 * @description
	 * Returns Sku EDD
	 */
	public getSkuDeliveryDate$(skuId: string): Observable<SkuEDDResponse> {
		return this.productDao.getSkuDeliveryDate$(skuId);
	}

	public getProductsBySkus$(skus: string[], retries = 0) {
		return this.productDao.getProductsBySkus$(skus, retries);
	}
}
